import * as THREE from './hp-three.module.js';
import { GLTFLoader } from './hp-gltf-loader.js';
import './hp-join-television.js';

// Deliberately reuse the theme's r160 TV geometry and material implementation.
// This component owns its lifecycle; the Join page's access state never runs.
const JoinTV = customElements.get('hp-join-tv');
const AUTOMATIC_PAUSES = new Set(['offscreen', 'hidden', 'reduced-motion']);
const VIDEO_EVENTS = ['loadeddata', 'playing', 'pause', 'ended', 'seeked', 'error'];

class HPStoryTV extends JoinTV {
  constructor() {
    super();
    this.connectionGeneration = 0;
    this.render = this.renderShowcase.bind(this);
  }

  connectedCallback() {
    if (this.connectionController) return;
    this.connectionGeneration += 1;
    const generation = this.connectionGeneration;
    this.connectionController = new AbortController();
    const { signal } = this.connectionController;
    this.fallback = this.querySelector('[data-tv-fallback]');
    this.closest('[data-story-tv]')?.querySelector('.hp-story-tv__frame')?.addEventListener('load', () => {
      this.cameraFitDirty = true;
      this.render();
    }, { signal });
    this.status = this.querySelector('[data-tv-status]');
    this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = this.mediaQuery.matches;
    // The landscape-phone rule can change while the stage width stays fixed.
    window.matchMedia('(min-width: 768px) and (max-height: 600px) and (hover: none) and (pointer: coarse)')
      .addEventListener('change', () => this.resize(), { signal });
    this.resources = { geometries: new Set(), materials: new Set(), textures: new Set() };
    this.channelMeshes = [];
    this.screenLayers = [];
    this.screenGlowMeshes = [];
    this.modelReady = false;
    this.startedLoading = false;
    this.hasStarted = false;
    this.completed = false;
    this.previousVideoTime = 0;
    this.replayHeadDuration = 0;
    this.mediaFailed = false;
    this.failed = false;
    this.screenHasContent = false;
    this.cameraFitDirty = true;
    this.cameraFitFront = null;
    this.cabinetVertices = null;
    this.userPlayback = false;
    this.pendingUserPlay = false;
    this.shouldResume = false;
    this.pauseReason = 'idle';
    this.playRequest = 0;
    this.playPending = false;
    this.isVisible = false;
    this.playbackVisible = false;
    this.visibilityThreshold = null;
    this.userRotation = 0;
    this.dragPointerId = null;
    this.dataset.rotation = '0';
    this.dataset.dragging = 'false';
    this.dataset.state = 'waiting';
    this.dataset.playback = 'idle';
    this.setAttribute('aria-keyshortcuts', 'ArrowLeft ArrowRight Home Space Enter R');
    this.setStatus('Loading the television…');
    // This raster is an error fallback, not a loading poster: its framing
    // differs from the responsive 3D view and would flash much larger.
    if (this.fallback) this.fallback.hidden = true;

    this.video = document.createElement('video');
    this.video.crossOrigin = 'anonymous';
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.playsInline = true;
    this.video.loop = true;
    this.video.preload = 'auto';
    this.video.hidden = true;
    this.video.setAttribute('muted', '');
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('aria-hidden', 'true');
    this.append(this.video);
    this.poster = new Image();
    this.poster.crossOrigin = 'anonymous';
    this.poster.addEventListener('load', () => {
      if (generation !== this.connectionGeneration) return;
      this.render();
    }, { signal });
    this.poster.addEventListener('error', () => {
      if (generation !== this.connectionGeneration) return;
      this.posterFailed = true;
      this.showFallback('The TV preview is unavailable.');
    }, { signal });
    for (const event of VIDEO_EVENTS) {
      this.video.addEventListener(event, () => {
        if (generation === this.connectionGeneration) this.handleVideoEvent(event);
      }, { signal });
    }

    this.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.addEventListener('pointermove', this.handlePointerMove, { signal });
    this.addEventListener('pointerup', this.handlePointerEnd, { signal });
    this.addEventListener('pointercancel', this.handlePointerEnd, { signal });
    this.addEventListener('keydown', this.handleKeyDown, { signal });
    document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
    this.onMotionChange = event => {
      this.reducedMotion = event.matches;
      if (event.matches) this.userPlayback = false;
      this.syncPlayback();
      this.render();
    };
    this.mediaQuery.addEventListener('change', this.onMotionChange);

    if ('IntersectionObserver' in window) {
      this.nearObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.nearObserver?.disconnect();
        this.initialize();
      }, { rootMargin: '300px' });
      this.nearObserver.observe(this);
      this.observeVisibility();
      window.addEventListener('resize', () => this.observeVisibility(), { signal });
      window.visualViewport?.addEventListener('resize', () => this.observeVisibility(), { signal });
    } else {
      this.isVisible = true;
      this.playbackVisible = true;
      this.initialize();
    }
    this.updatePlaybackState();
  }

  observeVisibility() {
    if (!this.connectionController || !('IntersectionObserver' in window)) return;
    const rect = this.getBoundingClientRect();
    const viewportWidth = window.visualViewport?.width || window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    // A zoomed TV may be much larger than the viewport. Require 15% of the
    // area that can actually fit, rather than an unreachable 15% of the TV.
    const availableArea = Math.min(rect.width, viewportWidth) * Math.min(rect.height, viewportHeight);
    const threshold = rect.width && rect.height ? .15 * availableArea / (rect.width * rect.height) : .15;
    if (this.visibilityObserver && Math.abs(this.visibilityThreshold - threshold) < 1e-8) return;
    this.visibilityObserver?.disconnect();
    this.visibilityThreshold = threshold;
    const observer = new IntersectionObserver(entries => {
      if (observer !== this.visibilityObserver || !this.connectionController) return;
      const entry = entries[0];
      this.isVisible = entry?.isIntersecting === true;
      // Some browsers round only the entry ratio to float32. Allow that
      // precision at the threshold so its sole crossing callback can play.
      const effectiveThreshold = observer.thresholds[observer.thresholds.length - 1];
      this.playbackVisible = this.isVisible
        && entry.intersectionRatio >= Math.min(effectiveThreshold, Math.fround(effectiveThreshold));
      this.syncPlayback();
      // Reveal a fitted poster as soon as the TV enters view. The stricter
      // playback gate still avoids autoplay for a couple of visible pixels.
      this.render();
    }, { threshold: [0, threshold] });
    this.visibilityObserver = observer;
    observer.observe(this);
  }

  initialize() {
    if (this.startedLoading || !this.connectionController || this.failed) return;
    this.startedLoading = true;
    this.dataset.state = 'loading';
    this.posterFailed = false;
    this.poster.src = this.dataset.poster || '';
    this.video.poster = this.dataset.poster || '';
    this.video.src = this.dataset.video || '';
    this.video.load();
    const generation = this.connectionGeneration;
    this.mediaLoadTimer = setTimeout(() => {
      if (generation === this.connectionGeneration && this.video.readyState < 2) {
        this.handleMediaFailure();
      }
    }, 15000);
    try {
      this.createStage();
      this.loadModel(generation);
    } catch (error) {
      this.showFallback('The TV preview is unavailable.', error);
    }
  }

  createStage() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(31, 1, 0.01, 100);
    this.camera.position.set(.8, 0, 8);
    this.camera.lookAt(0, 0, 0);
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.domElement.className = 'hp-story-tv__canvas';
    this.renderer.domElement.setAttribute('aria-hidden', 'true');
    this.renderer.domElement.addEventListener('webglcontextlost', event => {
      event.preventDefault();
      this.showFallback('The TV preview is unavailable.');
    }, { signal: this.connectionController.signal });
    this.prepend(this.renderer.domElement);
    this.stage = new THREE.Group();
    this.stage.rotation.y = Math.PI - 0.025;
    this.scene.add(this.stage);
    this.scene.add(new THREE.HemisphereLight(0xfff6df, 0x3852a6, 2.25));
    const keyLight = new THREE.DirectionalLight(0xffffff, 3.2);
    keyLight.position.set(4, 6, 7);
    this.scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(0x9db0ff, 2.1);
    rimLight.position.set(-5, 2, -4);
    this.scene.add(rimLight);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this);
    this.resize();
  }

  async loadModel(generation) {
    let expired = false;
    try {
      const deadline = new Promise((_, reject) => {
        this.modelLoadTimer = setTimeout(() => {
          expired = true;
          reject(new Error('The TV model took too long to load.'));
        }, 10000);
      });
      const loading = new GLTFLoader().loadAsync(this.dataset.model).then(gltf => {
        if (!expired && generation === this.connectionGeneration && this.isConnected && !this.failed) return gltf;
        this.disposeModel(gltf.scene);
        throw new Error('The TV model is no longer needed.');
      });
      const gltf = await Promise.race([loading, deadline]);
      if (generation !== this.connectionGeneration) {
        this.disposeModel(gltf.scene);
        return;
      }
      clearTimeout(this.modelLoadTimer);
      this.model = gltf.scene;
      this.collectResources(this.model);
      this.stage.add(this.model);
      this.model.traverse(object => {
        if (!object.isMesh || !/screennoise_1/i.test(object.name)) return;
        const material = Array.isArray(object.material) ? object.material[0] : object.material;
        this.nativeStaticAtlas = material.emissiveMap || material.map;
      });
      this.cleanTelevisionGeometry();
      this.cacheCabinetVertices();
      this.hideSceneProps();
      this.prepareChannelMeshes();
      if (!this.channelMeshes.length) throw new Error('The TV screen mesh is missing.');
      this.applyResponsiveModelLayout();
      this.screen = document.createElement('canvas');
      this.screen.width = 1220;
      this.screen.height = 1000;
      this.context = this.screen.getContext('2d');
      if (!this.context) throw new Error('The TV screen canvas is unavailable.');
      this.screenTexture = new THREE.CanvasTexture(this.screen);
      this.screenTexture.flipY = true;
      this.screenTexture.colorSpace = THREE.SRGBColorSpace;
      this.screenTexture.minFilter = THREE.LinearFilter;
      this.screenTexture.magFilter = THREE.LinearFilter;
      this.screenTexture.generateMipmaps = false;
      this.photoMaterial = this.createRetroScreenMaterial(this.screenTexture);
      this.channelMeshes.forEach(mesh => { mesh.material = this.photoMaterial; });
      this.createScreenGlow();
      this.createAttributionFloor();
      this.createSticker();
      this.collectResources(this.stage);
      this.modelReady = true;
      this.render();
      if (this.pendingUserPlay) {
        this.pendingUserPlay = false;
        this.requestPlayback(true);
      } else this.syncPlayback();
      this.updatePlaybackState();
    } catch (error) {
      if (generation !== this.connectionGeneration || !this.isConnected) return;
      clearTimeout(this.modelLoadTimer);
      this.showFallback('The TV preview is unavailable.', error);
    }
  }

  prepareChannelMeshes() {
    super.prepareChannelMeshes();
    this.model.traverse(object => {
      if (/^TV_screenchannel2(?:_|$)/i.test(object.name)) object.visible = false;
    });
  }

  createSticker() {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 512;
    const context = canvas.getContext('2d');
    const points = Array.from({ length: 32 }, (_, index) => {
      const angle = -Math.PI / 2 + index * Math.PI / 16;
      const radius = index % 2 ? .41 : .47;
      return new THREE.Vector2(Math.cos(angle) * radius, -Math.sin(angle) * radius);
    });
    this.stickerTexture = new THREE.CanvasTexture(canvas);
    this.stickerTexture.colorSpace = THREE.SRGBColorSpace;
    this.stickerTexture.anisotropy = Math.min(4, this.renderer.capabilities.getMaxAnisotropy());
    const draw = () => {
      context.clearRect(0, 0, 512, 512);
      context.save();
      context.scale(512, 512);
      const outline = (scale = 1) => {
        context.beginPath();
        points.forEach((point, index) => {
          context[index ? 'lineTo' : 'moveTo'](.5 + point.x * scale, .5 - point.y * scale);
        });
        context.closePath();
      };
      outline();
      context.fillStyle = '#b7ed4b';
      context.fill();
      context.lineJoin = 'round';
      context.strokeStyle = '#f4f5ea';
      context.lineWidth = .025;
      context.stroke();
      outline(.88);
      context.strokeStyle = '#101414';
      context.lineWidth = .0125;
      context.stroke();
      context.restore();
      context.fillStyle = '#101414';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = '400 118px "Bowlby One SC", Impact, sans-serif';
      context.fillText('20%', 256, 230);
      context.font = '400 82px "Bowlby One SC", Impact, sans-serif';
      context.fillText('OFF', 256, 328);
      this.stickerTexture.needsUpdate = true;
    };
    draw();
    // Follow the star's outline so the paper back and full-turn fit have no
    // invisible square corners. Leave room for the artwork's white border.
    const outline = new THREE.ShapeGeometry(new THREE.Shape(points.map(point => point.clone().multiplyScalar(1.035))));
    const flat = outline.getAttribute('position');
    const vertices = [];
    const indices = [];
    const vertexIds = new Map();
    const vertexId = point => {
      const key = `${point.x.toFixed(6)},${point.y.toFixed(6)}`;
      if (!vertexIds.has(key)) {
        vertexIds.set(key, vertices.length / 3);
        vertices.push(point.x, point.y, 0);
      }
      return vertexIds.get(key);
    };
    const subdivide = (a, b, c, depth) => {
      if (!depth) {
        indices.push(vertexId(a), vertexId(b), vertexId(c));
        return;
      }
      const ab = a.clone().lerp(b, .5);
      const bc = b.clone().lerp(c, .5);
      const ca = c.clone().lerp(a, .5);
      subdivide(a, ab, ca, depth - 1);
      subdivide(ab, b, bc, depth - 1);
      subdivide(ca, bc, c, depth - 1);
      subdivide(ab, bc, ca, depth - 1);
    };
    for (let index = 0; index < outline.index.count; index += 3) {
      subdivide(...[0, 1, 2].map(corner => new THREE.Vector3().fromBufferAttribute(flat, outline.index.getX(index + corner))), 3);
    }
    outline.dispose();
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(vertices.length / 3 * 2, 2));
    geometry.setIndex(indices);
    const positions = geometry.getAttribute('position');
    this.stickerFlatPositions = positions.array.slice();
    const uv = geometry.getAttribute('uv');
    for (let index = 0; index < positions.count; index += 1) {
      uv.setXY(index, positions.getX(index) + .5, positions.getY(index) + .5);
    }
    this.stickerMesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
      map: this.stickerTexture, transparent: true, alphaTest: .1, toneMapped: false
    }));
    this.stickerMesh.name = 'StoryOfferSticker';
    // Show unprinted paper on the overhanging part when the TV turns around.
    this.stickerBacking = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
      color: '#f4f5ea', side: THREE.BackSide, toneMapped: false
    }));
    this.stickerBacking.name = 'StoryOfferStickerBacking';
    this.stickerPivot = new THREE.Group();
    this.stickerPivot.add(this.stickerMesh, this.stickerBacking);
    this.stage.add(this.stickerPivot);
    this.updateStickerLayout();
    const generation = this.connectionGeneration;
    document.fonts?.ready.then(() => {
      if (generation !== this.connectionGeneration || !this.connectionController) return;
      draw();
      this.render();
    });
  }

  updateStickerLayout() {
    if (!this.stickerPivot || !this.cabinetVertices || !this.channelMeshes.length) return;
    const { mesh, positions } = this.cabinetVertices;
    mesh.updateWorldMatrix(true, false);
    const worldToStage = this.stage.matrixWorld.clone().invert();
    const meshToStage = worldToStage.clone().multiply(mesh.matrixWorld);
    const bounds = new THREE.Box3().setFromPoints(
      positions.map(point => point.clone().applyMatrix4(meshToStage))
    );
    const stickerScale = Number.parseFloat(getComputedStyle(this).getPropertyValue('--hp-tv-sticker-scale')) || .9;
    const size = (bounds.max.x - bounds.min.x) * .36 * stickerScale;
    const anchor = new THREE.Vector3();
    const normal = new THREE.Vector3();
    let closest = Infinity;
    for (const screen of this.channelMeshes) {
      screen.updateWorldMatrix(true, false);
      const toStage = worldToStage.clone().multiply(screen.matrixWorld);
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(toStage);
      const geometry = screen.geometry;
      const uv = geometry.getAttribute('uv');
      for (let index = 0; index < uv.count; index += 1) {
        const distance = (1 - uv.getX(index)) ** 2 + (1 - uv.getY(index)) ** 2;
        if (distance >= closest) continue;
        closest = distance;
        anchor.fromBufferAttribute(geometry.getAttribute('position'), index).applyMatrix4(toStage);
        normal.fromBufferAttribute(geometry.getAttribute('normal'), index).applyNormalMatrix(normalMatrix);
      }
    }
    const raycaster = new THREE.Raycaster();
    const surfaces = [mesh, ...this.channelMeshes];
    const sample = (point, outward) => {
      const origin = point.clone().addScaledVector(outward, size * 2).applyMatrix4(this.stage.matrixWorld);
      const direction = outward.clone().negate().transformDirection(this.stage.matrixWorld);
      raycaster.set(origin, direction);
      raycaster.far = size * 4;
      const hit = raycaster.intersectObjects(surfaces, false)[0];
      if (!hit) return null;
      return {
        point: hit.point.applyMatrix4(worldToStage),
        normal: hit.face.normal.clone().applyNormalMatrix(
          new THREE.Matrix3().getNormalMatrix(worldToStage.clone().multiply(hit.object.matrixWorld))
        )
      };
    };
    // UV (1, 1) locates the screen corner. Its glass extends under the bezel,
    // so use the front surface at that XY rather than the recessed glass Z.
    const contact = sample(anchor, new THREE.Vector3(0, 0, -1));
    if (contact) {
      anchor.copy(contact.point);
      normal.copy(contact.normal);
    }
    const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), normal).normalize();
    const up = new THREE.Vector3().crossVectors(normal, right).normalize();
    this.stickerPivot.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(right, up, normal));
    this.stickerPivot.rotateZ(-5 * Math.PI / 180);
    this.stickerPivot.position.copy(anchor).addScaledVector(normal, size * .002);
    this.stickerPivot.scale.setScalar(size);
    this.stickerPivot.updateMatrix();
    const toSticker = this.stickerPivot.matrix.clone().invert();
    const geometry = this.stickerMesh.geometry;
    const paper = geometry.getAttribute('position');
    const contacts = [];
    const unsupported = [];
    for (let index = 0; index < paper.count; index += 1) {
      const flat = new THREE.Vector3().fromArray(this.stickerFlatPositions, index * 3);
      const hit = sample(flat.clone().applyMatrix4(this.stickerPivot.matrix), normal);
      const depth = hit?.point.applyMatrix4(toSticker).z;
      // Follow the curved front, without folding over steep cabinet edges.
      // Unsupported paper continues from the nearest supported contact.
      if (depth !== undefined && hit.normal.dot(normal) >= .65 && depth >= -.12 && depth <= .12) {
        // This thin clearance also keeps triangle interiors above rim edges.
        flat.z = depth + .008;
        contacts.push(flat);
      } else unsupported.push({ index, flat });
      paper.setXYZ(index, flat.x, flat.y, flat.z);
    }
    for (const { index, flat } of unsupported) {
      let nearest = Infinity;
      for (const contact of contacts) {
        const distance = (contact.x - flat.x) ** 2 + (contact.y - flat.y) ** 2;
        if (distance >= nearest) continue;
        nearest = distance;
        flat.z = contact.z;
      }
      paper.setZ(index, flat.z);
    }
    paper.needsUpdate = true;
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    this.cameraFitDirty = true;
  }

  createAttributionFloor() {
    // Reuse the credit text and resources, then mount it on the cabinet's
    // back so it remains readable from the level camera when the TV turns.
    super.createAttributionFloor();
    const positions = this.attributionGeometry?.getAttribute('position');
    const uv = this.attributionGeometry?.getAttribute('uv');
    if (positions && uv) {
      for (let index = 0; index < positions.count; index += 1) {
        positions.setXYZ(index, uv.getX(index) - .5, uv.getY(index) - .5, 0);
      }
      positions.needsUpdate = true;
      this.attributionGeometry.computeBoundingSphere();
    }
    this.attributionMaterial?.color.set('#ffffff');
    const generation = this.connectionGeneration;
    document.fonts?.ready.then(() => {
      if (generation === this.connectionGeneration && this.connectionController) this.render();
    });
  }

  updateAttributionFloorLayout() {
    if (!this.attributionMesh || !this.cabinetVertices) return;
    const cabinet = this.cabinetVertices;
    cabinet.mesh.updateWorldMatrix(true, false);
    // Measure in stage space so resizing at any yaw gives the same label.
    const meshToStage = this.stage.matrixWorld.clone().invert().multiply(cabinet.mesh.matrixWorld);
    const bounds = new THREE.Box3().setFromPoints(
      cabinet.positions.map(point => point.clone().applyMatrix4(meshToStage))
    );
    const width = (bounds.max.x - bounds.min.x) * .68;
    const depth = width * (360 / 1536);
    this.attributionMesh.scale.set(width, depth, 1);
    this.attributionMesh.rotation.set(0, 0, 0);
    this.attributionMesh.position.set(0, 0, 0);
    this.attributionPivot.rotation.set(0, 0, 0);
    this.attributionPivot.position.set(
      (bounds.min.x + bounds.max.x) / 2,
      bounds.min.y + (bounds.max.y - bounds.min.y) * .25,
      bounds.max.z + .015
    );
  }

  togglePlayback() {
    if (this.completed) this.replay();
    else if (!this.video?.paused || this.playPending) this.pausePlayback('manual');
    else this.requestPlayback(true);
  }

  replay() {
    if (!this.hasStarted || this.failed || this.mediaFailed) return;
    this.completed = false;
    this.pauseReason = 'manual';
    this.previousVideoTime = 0;
    this.replayHeadDuration = .32;
    this.video.currentTime = 0;
    this.requestPlayback(true);
    this.render();
  }

  handlePointerDown(event) {
    if (this.failed || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
    this.tapStart = { x: event.clientX, y: event.clientY, moved: false };
    super.handlePointerDown(event);
  }

  handlePointerMove(event) {
    if (event.pointerId === this.dragPointerId && this.tapStart) {
      this.tapStart.moved ||= Math.hypot(event.clientX - this.tapStart.x, event.clientY - this.tapStart.y) > 6;
    }
    super.handlePointerMove(event);
  }

  handlePointerEnd(event) {
    const tap = event.type === 'pointerup' && event.pointerId === this.dragPointerId
      && this.tapStart && !this.tapStart.moved
      && Math.hypot(event.clientX - this.tapStart.x, event.clientY - this.tapStart.y) <= 6;
    super.handlePointerEnd(event);
    this.tapStart = null;
    // A touch intended to turn the model must not silently pause the loop.
    // Keyboard Space still provides an explicit pause action.
    if (tap && this.video?.paused) this.requestPlayback(true);
  }

  handleKeyDown(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if ([' ', 'Enter', 'r', 'R'].includes(event.key)) {
      event.preventDefault();
      if (!event.repeat) {
        if (event.key.toLowerCase() === 'r') this.replay();
        else this.togglePlayback();
      }
      return;
    }
    super.handleKeyDown(event);
  }

  createRetroScreenMaterial(texture) {
    const material = super.createRetroScreenMaterial(texture);
    material.uniforms.uReplayGlitch = { value: 0 };
    material.fragmentShader = material.fragmentShader
      .replace('uniform float uTime;', 'uniform float uTime;\n uniform float uReplayGlitch;')
      .replace('vec2 uv = vUv;', `vec2 uv = vUv;
        float rowNoise = grainAt(vec2(floor(uv.y * 18.0), floor(uTime * 24.0)));
        if(uReplayGlitch > 0.0)uv.x = fract(uv.x + uReplayGlitch * step(.82, rowNoise) * (rowNoise - .5) * .12);`)
      .replaceAll('.0015', '.00045')
      .replaceAll('.006', '.003')
      .replace('phosphor * .035', 'phosphor * .016')
      .replace('colour += (staticPattern - .5) * uNoiseAmount;', `colour += (staticPattern - .5) * uNoiseAmount;
        // Reuse the GLB's three native static tiles and its corner-fill grain.
        float roll = 1.0 - smoothstep(.015, .10, abs(fract(vUv.y + uTime * 1.7) - .5));
        vec3 replayStatic = vec3(.13 + .58 * staticPattern) * (1.0 - .48 * roll);
        colour = mix(colour, replayStatic, uReplayGlitch * .96);`);
    return material;
  }

  replayGlitchAmount() {
    const { currentTime: time, duration } = this.video;
    if (!this.hasStarted || this.completed || this.mediaFailed || this.reducedMotion
      || !Number.isFinite(duration) || duration <= 0) {
      this.previousVideoTime = time;
      this.replayHeadDuration = 0;
      return 0;
    }
    // A seek can briefly lose decoded data; retain its pending replay fade.
    if (this.video.readyState < 2) return 0;
    // Cover the native loop boundary without changing the 15-second video.
    if (this.previousVideoTime > duration - .5 && time < .25 && !this.video.paused) {
      this.replayHeadDuration = .12;
    }
    this.previousVideoTime = time;
    if (time >= this.replayHeadDuration) this.replayHeadDuration = 0;
    const tail = THREE.MathUtils.smoothstep(time, duration - .42, duration - .33);
    const head = this.replayHeadDuration
      ? 1 - THREE.MathUtils.smoothstep(time, 0, this.replayHeadDuration) : 0;
    return Math.max(tail, head);
  }

  applyResponsiveModelLayout() {
    if (!this.model) return;
    const flag = getComputedStyle(this).getPropertyValue('--hp-tv-compact').trim();
    const compact = flag ? Number.parseFloat(flag) === 1 : this.clientWidth <= 650;
    this.model.traverse(object => {
      if (/^VHS_Reader(?:\.?001)_Tv(?:_|$)/i.test(object.name)) object.visible = !compact;
    });
    this.dataset.tapesVisible = String(!compact);
    // Join's fit works directly in model space, so repeated resizing is stable.
    super.fitModel();
    this.updateStickerLayout();
    this.cameraFitDirty = true;
  }

  cacheCabinetVertices() {
    this.cabinetVertices = null;
    this.model.traverse(mesh => {
      if (!mesh.isMesh || !/^TV_Tv(?:_|$)/i.test(mesh.name)) return;
      const positions = mesh.geometry.getAttribute('position');
      const index = mesh.geometry.getIndex();
      if (!positions) return;
      // The source combines the cabinet and aerials in one mesh. Weld the
      // cleaned triangles by position so the largest connected component is
      // the cabinet, without relying on a height cutoff or antenna pose.
      const welded = new Map();
      const vertices = [];
      const parents = [];
      const vertexIds = [];
      for (let offset = 0; offset < positions.count; offset += 1) {
        const vertex = new THREE.Vector3().fromBufferAttribute(positions, offset);
        const key = [vertex.x, vertex.y, vertex.z].map(value => value.toFixed(5)).join(',');
        let id = welded.get(key);
        if (id === undefined) {
          id = vertices.length;
          welded.set(key, id);
          vertices.push(vertex);
          parents.push(id);
        }
        vertexIds.push(id);
      }
      const root = id => {
        while (parents[id] !== id) {
          parents[id] = parents[parents[id]];
          id = parents[id];
        }
        return id;
      };
      const triangleRoots = [];
      const count = index ? index.count : positions.count;
      for (let offset = 0; offset < count; offset += 3) {
        const ids = [0, 1, 2].map(corner => vertexIds[index ? index.getX(offset + corner) : offset + corner]);
        parents[root(ids[1])] = root(ids[0]);
        parents[root(ids[2])] = root(ids[0]);
        triangleRoots.push(ids[0]);
      }
      const components = new Map();
      for (const id of triangleRoots) {
        const component = root(id);
        components.set(component, (components.get(component) || 0) + 1);
      }
      const largest = [...components].sort((a, b) => b[1] - a[1])[0]?.[0];
      if (largest === undefined) return;
      this.cabinetVertices = { mesh, positions: vertices.filter((_, id) => root(id) === largest) };
    });
  }

  getCabinetBounds() {
    const canvas = this.renderer?.domElement;
    if (!this.cabinetVertices || !this.camera || !canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const { mesh, positions } = this.cabinetVertices;
    mesh.updateWorldMatrix(true, false);
    this.camera.updateMatrixWorld();
    const point = new THREE.Vector3();
    const bounds = { top: Infinity, bottom: -Infinity, left: Infinity, right: -Infinity };
    for (const position of positions) {
      point.copy(position).applyMatrix4(mesh.matrixWorld).project(this.camera);
      const x = rect.left + (point.x + 1) * rect.width / 2;
      const y = rect.top + (1 - point.y) * rect.height / 2;
      bounds.left = Math.min(bounds.left, x);
      bounds.right = Math.max(bounds.right, x);
      bounds.top = Math.min(bounds.top, y);
      bounds.bottom = Math.max(bounds.bottom, y);
    }
    bounds.alignmentY = (bounds.top + bounds.bottom) / 2;
    return Number.isFinite(bounds.top) ? bounds : null;
  }

  getFrameBounds() {
    const canvas = this.renderer?.domElement;
    const canvasRect = canvas?.getBoundingClientRect() || this.getBoundingClientRect();
    if (!canvasRect.width || !canvasRect.height) return null;
    const image = this.closest('[data-story-tv]')?.querySelector('.hp-story-tv__frame');
    const imageRect = image?.getBoundingClientRect();
    let left = canvasRect.left;
    let top = canvasRect.top;
    let width = canvasRect.width;
    let height = canvasRect.height;
    if (imageRect?.width && imageRect.height) {
      const style = getComputedStyle(image);
      const intrinsicWidth = image.naturalWidth || Number(image.getAttribute('width'));
      const intrinsicHeight = image.naturalHeight || Number(image.getAttribute('height'));
      left = imageRect.left;
      top = imageRect.top;
      width = imageRect.width;
      height = imageRect.height;
      // object-fit:contain can leave empty space inside the image element.
      // Use the painted image's displayed content, rather than that element box.
      if (intrinsicWidth && intrinsicHeight && ['contain', 'scale-down', 'none'].includes(style.objectFit)) {
        let scale = style.objectFit === 'none' ? 1
          : Math.min(imageRect.width / intrinsicWidth, imageRect.height / intrinsicHeight);
        if (style.objectFit === 'scale-down') scale = Math.min(1, scale);
        width = intrinsicWidth * scale;
        height = intrinsicHeight * scale;
        const [positionX = '50%', positionY = '50%'] = style.objectPosition.split(/\s+/);
        const offset = (position, freeSpace) => {
          if (position.endsWith('%')) return freeSpace * Number.parseFloat(position) / 100;
          if (position.endsWith('px')) return Number.parseFloat(position);
          if (position === 'left' || position === 'top') return 0;
          if (position === 'right' || position === 'bottom') return freeSpace;
          return freeSpace / 2;
        };
        left += offset(positionX, imageRect.width - width);
        top += offset(positionY, imageRect.height - height);
      }
      // Replaced images clip overflowing content, including object-fit:cover.
      const right = Math.min(left + width, imageRect.right);
      const bottom = Math.min(top + height, imageRect.bottom);
      left = Math.max(left, imageRect.left);
      top = Math.max(top, imageRect.top);
      width = Math.max(0, right - left);
      height = Math.max(0, bottom - top);
    }
    const bounds = { left, top, right: left + width, bottom: top + height, width, height };
    // Reserve the rough outer brush strokes while also protecting the canvas.
    const inset = .04;
    const safe = {
      left: Math.max(bounds.left + width * inset, canvasRect.left),
      right: Math.min(bounds.right - width * inset, canvasRect.right),
      top: Math.max(bounds.top + height * inset, canvasRect.top),
      bottom: Math.min(bounds.bottom - height * inset, canvasRect.bottom)
    };
    safe.width = safe.right - safe.left;
    safe.height = safe.bottom - safe.top;
    return { ...bounds, safe };
  }

  fitCamera() {
    if (!this.model || !this.camera) return;
    // Reserve a complete turn when the layout changes. Refitting to each
    // angle makes the camera zoom in on narrow views and out on wider ones.
    if (!this.cameraFitDirty && this.cameraFitFront === Boolean(this.front)) return;
    const canvas = this.renderer?.domElement;
    const canvasRect = canvas?.getBoundingClientRect();
    const frame = this.getFrameBounds()?.safe;
    if (!canvasRect?.width || !canvasRect.height || !frame || frame.width <= 0 || frame.height <= 0) return;
    // Always align using the starting view, including after resizing while
    // turned around. Dragging changes only the stage, never the camera.
    const rotation = this.stage.rotation.y;
    this.stage.rotation.y = Math.PI - .025;
    this.stage.updateWorldMatrix(true, true);
    const points = [];
    const bounds = new THREE.Box3();
    const collect = (root, target, expandBounds = false) => {
      root?.traverse(mesh => {
        if (!mesh.isMesh || mesh.userData.joinScreenGlow) return;
        for (let ancestor = mesh; ancestor; ancestor = ancestor.parent) if (!ancestor.visible) return;
        const positions = mesh.geometry.getAttribute('position');
        if (!positions) return;
        for (let index = 0; index < positions.count; index += 1) {
          const point = new THREE.Vector3().fromBufferAttribute(positions, index).applyMatrix4(mesh.matrixWorld);
          target.push(point);
          if (expandBounds) bounds.expandByPoint(point);
        }
      });
    };
    collect(this.model, points, true);
    const cabinet = this.cabinetVertices;
    const cabinetPoints = cabinet
      ? cabinet.positions.map(point => point.clone().applyMatrix4(cabinet.mesh.matrixWorld))
      : points;
    const fitPoints = points.slice();
    // Attached artwork must fit too, without moving the TV's visual alignment.
    collect(this.attributionMesh, fitPoints);
    collect(this.stickerPivot, fitPoints);
    this.stage.rotation.y = rotation;
    this.stage.updateWorldMatrix(true, true);
    if (bounds.isEmpty()) return;
    const centerCabinet = Number.parseFloat(getComputedStyle(this).getPropertyValue('--hp-tv-center-cabinet')) === 1;
    const verticalPoints = centerCabinet ? cabinetPoints : points;
    const center = bounds.getCenter(new THREE.Vector3());
    center.y = new THREE.Box3().setFromPoints(verticalPoints).getCenter(new THREE.Vector3()).y;
    // Keep the screen upright at eye level, with just enough yaw to show its
    // depth. The full-turn fit below chooses the closest contained distance.
    const direction = new THREE.Vector3(...(this.front ? [0, 0, 8] : [.8, 0, 8])).normalize();
    const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), direction).normalize();
    const up = new THREE.Vector3().crossVectors(direction, right).normalize();
    const halfHeight = Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
    const halfWidth = halfHeight * this.camera.aspect;
    const relative = new THREE.Vector3();
    const cameraPoint = point => {
      relative.copy(point).sub(center);
      return { x: relative.dot(right), y: relative.dot(up), z: relative.dot(direction) };
    };
    const horizontalPoints = points.map(cameraPoint);
    const alignmentPoints = verticalPoints.map(cameraPoint);
    // Each vertex sweeps a horizontal circle around the stage's Y axis.
    // Its radius and height describe every possible yaw, without sampling
    // angles or enlarging a world-space box on repeated rotations.
    const orbits = fitPoints.map(point => ({
      radius: Math.hypot(point.x - this.stage.position.x, point.z - this.stage.position.z),
      y: point.y - this.stage.position.y
    }));
    const orbitCenter = center.clone().sub(this.stage.position);
    const limits = {
      left: (frame.left - canvasRect.left) * 2 / canvasRect.width - 1,
      right: (frame.right - canvasRect.left) * 2 / canvasRect.width - 1,
      top: 1 - (frame.top - canvasRect.top) * 2 / canvasRect.height,
      bottom: 1 - (frame.bottom - canvasRect.top) * 2 / canvasRect.height
    };
    const targetX = (limits.left + limits.right) / 2;
    const targetY = (limits.top + limits.bottom) / 2;
    const measure = distance => {
      let leftEdge = Infinity;
      let rightEdge = -Infinity;
      let top = -Infinity;
      let bottom = Infinity;
      for (const point of horizontalPoints) {
        const x = point.x / ((distance - point.z) * halfWidth);
        leftEdge = Math.min(leftEdge, x);
        rightEdge = Math.max(rightEdge, x);
      }
      for (const point of alignmentPoints) {
        const y = point.y / ((distance - point.z) * halfHeight);
        top = Math.max(top, y);
        bottom = Math.min(bottom, y);
      }
      const shiftX = targetX - (leftEdge + rightEdge) / 2;
      const shiftY = targetY - (top + bottom) / 2;
      const planes = [
        [right, 1, (limits.right - shiftX) * halfWidth],
        [right, -1, (shiftX - limits.left) * halfWidth],
        [up, 1, (limits.top - shiftY) * halfHeight],
        [up, -1, (shiftY - limits.bottom) * halfHeight]
      ];
      const fits = planes.every(([axis, sign, edge]) => {
        if (edge <= 0) return false;
        const normal = axis.clone().multiplyScalar(sign).addScaledVector(direction, edge);
        const radial = Math.hypot(normal.x, normal.z);
        const limit = edge * distance + normal.dot(orbitCenter);
        // Exact maximum of this plane expression over each circular orbit.
        return orbits.every(point => normal.y * point.y + radial * point.radius <= limit);
      });
      return { fits, shiftX, shiftY };
    };
    // Fit the entire swept silhouette, with the starting view's alignment.
    const depthRadius = Math.hypot(direction.x, direction.z);
    let lower = Math.max(.01, ...orbits.map(point => direction.y * point.y
      + depthRadius * point.radius - direction.dot(orbitCenter) + this.camera.near));
    let upper = Math.max(1, lower * 2);
    while (!measure(upper).fits) upper *= 2;
    for (let iteration = 0; iteration < 22; iteration += 1) {
      const middle = (lower + upper) / 2;
      if (measure(middle).fits) upper = middle;
      else lower = middle;
    }
    const distance = upper;
    const { shiftX, shiftY } = measure(distance);
    this.camera.position.copy(center).addScaledVector(direction, distance);
    this.camera.lookAt(center);
    this.camera.updateMatrixWorld();
    this.camera.clearViewOffset();
    this.camera.setViewOffset(this.clientWidth, this.clientHeight,
      -shiftX * this.clientWidth / 2, shiftY * this.clientHeight / 2,
      this.clientWidth, this.clientHeight);
    this.cameraFitFront = Boolean(this.front);
    this.cameraFitDirty = false;
  }

  resize() {
    this.observeVisibility();
    if (!this.renderer || !this.camera || !this.clientWidth || !this.clientHeight || this.failed) return;
    this.camera.aspect = this.clientWidth / this.clientHeight;
    this.camera.updateProjectionMatrix();
    this.cameraFitDirty = true;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    this.renderer.setSize(this.clientWidth, this.clientHeight, false);
    this.applyResponsiveModelLayout();
    this.drawAttribution?.();
    this.render();
  }

  drawScreen() {
    if (!this.context || !this.screenTexture) return false;
    const showVideo = this.hasStarted && !this.completed && !this.mediaFailed
      && (!this.reducedMotion || this.userPlayback) && this.video.readyState >= 2;
    const source = showVideo ? this.video : this.poster?.complete && this.poster.naturalWidth ? this.poster : null;
    if (!source) return false;
    try {
      this.context.fillStyle = '#101414';
      this.context.fillRect(0, 0, 1220, 1000);
      this.context.drawImage(source, 0, 0, 1220, 1000);
      this.screenTexture.needsUpdate = true;
      this.screenHasContent = true;
      return true;
    } catch (error) {
      if (source === this.video) this.handleMediaFailure();
      else this.showFallback('The TV preview is unavailable.', error);
      return false;
    }
  }

  renderShowcase() {
    this.stopRendering();
    if (!this.renderer || !this.modelReady || this.failed || !this.isVisible || document.hidden) return;
    this.stage.rotation.y = Math.PI - .025 + this.userRotation;
    this.fitCamera();
    // Never reveal the initial camera if the frame has not been laid out yet.
    if (this.cameraFitDirty) return;
    if (!this.drawScreen()) return;
    this.updateCrtSignal((this.completed ? 14 : this.video.currentTime) * 1000);
    if (this.photoMaterial?.uniforms) {
      this.photoMaterial.uniforms.uNoiseAmount.value *= .5;
      this.photoMaterial.uniforms.uEmission.value = 1.03;
      this.photoMaterial.uniforms.uReplayGlitch.value = this.replayGlitchAmount();
    }
    try {
      this.renderer.render(this.scene, this.camera);
    } catch (error) {
      this.showFallback('The TV preview is unavailable.', error);
      return;
    }
    if (this.dataset.state !== 'ready') {
      this.dataset.state = 'ready';
      if (this.fallback) this.fallback.hidden = true;
      if (!this.mediaFailed && this.pauseReason !== 'blocked') this.setStatus('');
    }
    if (!this.video.paused && !this.video.ended && !this.completed) {
      this.crtTimer = setTimeout(() => { this.raf = requestAnimationFrame(this.render); }, 1000 / 24);
    }
  }

  stopRendering() {
    clearTimeout(this.crtTimer);
    cancelAnimationFrame(this.raf);
    this.crtTimer = null;
    this.raf = null;
  }

  canPlay() {
    return this.isVisible && this.playbackVisible && !document.hidden && (!this.reducedMotion || this.userPlayback);
  }

  syncPlayback() {
    if (!this.video || this.failed || this.completed || this.mediaFailed) return;
    if (!this.canPlay()) {
      const reason = document.hidden ? 'hidden' : !this.playbackVisible ? 'offscreen' : 'reduced-motion';
      if (!this.video.paused || this.playPending) this.pausePlayback(reason);
      else if (this.shouldResume && AUTOMATIC_PAUSES.has(this.pauseReason)) this.pauseReason = reason;
      this.stopRendering();
      return;
    }
    if (!this.modelReady || ['manual', 'blocked', 'error'].includes(this.pauseReason)) return;
    if (!this.hasStarted || this.shouldResume) this.requestPlayback(false);
  }

  async requestPlayback(explicit) {
    if (!this.video || this.completed || this.failed || this.mediaFailed) return;
    if (explicit) this.userPlayback = true;
    if (!this.startedLoading) this.initialize();
    if (!this.modelReady) {
      if (explicit) this.pendingUserPlay = true;
      return;
    }
    if (!this.canPlay() || this.playPending || !this.video.paused) return;
    this.pauseReason = null;
    this.shouldResume = false;
    this.playPending = true;
    const request = ++this.playRequest;
    const generation = this.connectionGeneration;
    this.updatePlaybackState();
    try {
      await this.video.play();
      if (generation !== this.connectionGeneration || request !== this.playRequest) return;
      this.playPending = false;
      this.setStatus('');
      this.render();
      this.updatePlaybackState();
    } catch (error) {
      if (generation !== this.connectionGeneration || request !== this.playRequest) return;
      this.playPending = false;
      this.pauseReason = 'blocked';
      this.shouldResume = false;
      this.setStatus('Tap the TV or press Space to watch the board being built.');
      this.updatePlaybackState();
      this.render();
    }
  }

  pausePlayback(reason) {
    if (!this.video) return;
    const wasPlaying = !this.video.paused || this.playPending;
    this.shouldResume = AUTOMATIC_PAUSES.has(reason) && (wasPlaying || this.shouldResume);
    this.pauseReason = reason;
    this.playRequest += 1;
    this.playPending = false;
    this.video.pause();
    this.stopRendering();
    this.updatePlaybackState();
    if (reason === 'manual' || reason === 'reduced-motion') this.render();
  }

  handleVideoEvent(event) {
    if (event === 'error') {
      this.handleMediaFailure();
      return;
    }
    if (event === 'loadeddata') {
      clearTimeout(this.mediaLoadTimer);
      this.syncPlayback();
    } else if (event === 'playing') {
      this.hasStarted = true;
      this.pauseReason = null;
      if (!this.canPlay()) this.syncPlayback();
    } else if (event === 'pause' && !this.completed && this.pauseReason === null) {
      // Browser-initiated pauses must not create an automatic restart loop.
      this.pauseReason = 'manual';
      this.shouldResume = false;
    } else if (event === 'ended') {
      // Native looping normally wraps without ended. Recover if a browser
      // reaches an ended state so the visible showcase does not freeze.
      this.completed = false;
      this.pauseReason = null;
      this.shouldResume = true;
      this.playPending = false;
      this.playRequest += 1;
      this.setStatus('');
      this.syncPlayback();
    }
    this.updatePlaybackState();
    this.render();
  }

  handleMediaFailure() {
    if (this.mediaFailed || this.failed) return;
    clearTimeout(this.mediaLoadTimer);
    this.mediaFailed = true;
    this.pausePlayback('error');
    this.setStatus('The broadcast is unavailable. Here is the completed board.');
    if (this.posterFailed) this.showFallback('The TV preview is unavailable.');
    else this.render();
  }

  updatePlaybackState() {
    if (!this.video) return;
    const playing = !this.video.paused || this.playPending;
    this.dataset.playback = this.completed ? 'ended' : this.mediaFailed ? 'error' : playing ? 'playing' : this.pauseReason || 'paused';
  }

  setStatus(message) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = !message;
  }

  showFallback(message, error) {
    if (!this.connectionController) return;
    this.failed = true;
    this.pausePlayback('error');
    this.dataset.state = 'fallback';
    if (this.fallback) this.fallback.hidden = false;
    if (this.renderer) this.renderer.domElement.hidden = true;
    this.setStatus(message);
    this.updatePlaybackState();
    if (error) console.warn('The board-build television could not be shown.', error);
  }

  collectResources(model, resources = this.resources) {
    if (!model || !resources) return resources;
    model.traverse(object => {
      if (object.geometry) resources.geometries.add(object.geometry);
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials.filter(Boolean)) {
        resources.materials.add(material);
        for (const value of Object.values(material)) if (value?.isTexture) resources.textures.add(value);
        for (const uniform of Object.values(material.uniforms || {})) {
          if (uniform.value?.isTexture) resources.textures.add(uniform.value);
        }
      }
    });
    return resources;
  }

  disposeResources(resources) {
    if (!resources) return;
    const images = new Set();
    resources.geometries.forEach(geometry => geometry.dispose());
    resources.materials.forEach(material => material.dispose());
    resources.textures.forEach(texture => {
      const image = texture.source?.data;
      if (image?.close && !images.has(image)) { images.add(image); image.close(); }
      texture.dispose();
    });
    for (const set of Object.values(resources)) set.clear();
  }

  disposeModel(model) {
    this.disposeResources(this.collectResources(model, {
      geometries: new Set(), materials: new Set(), textures: new Set()
    }));
  }

  disconnectedCallback() {
    if (!this.connectionController) return;
    this.connectionGeneration += 1;
    this.playRequest += 1;
    this.connectionController.abort();
    this.connectionController = null;
    this.nearObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
    this.visibilityThreshold = null;
    this.resizeObserver?.disconnect();
    this.mediaQuery?.removeEventListener('change', this.onMotionChange);
    clearTimeout(this.modelLoadTimer);
    clearTimeout(this.mediaLoadTimer);
    this.stopRendering();
    if (this.dragPointerId !== null && this.hasPointerCapture?.(this.dragPointerId)) this.releasePointerCapture(this.dragPointerId);
    this.video?.pause();
    this.video?.removeAttribute('src');
    this.video?.load();
    this.video?.remove();
    if (this.poster) this.poster.src = '';
    this.collectResources(this.stage);
    if (this.screenTexture) this.resources.textures.add(this.screenTexture);
    if (this.photoMaterial) this.resources.materials.add(this.photoMaterial);
    this.disposeResources(this.resources);
    this.renderer?.dispose();
    this.renderer?.domElement.remove();
    this.renderer = null;
    this.scene = null;
    this.stage = null;
    this.camera = null;
    this.model = null;
    this.video = null;
    this.poster = null;
    this.screen = null;
    this.context = null;
    this.screenTexture = null;
    this.photoMaterial = null;
    this.nativeStaticAtlas = null;
    this.attributionTexture = null;
    this.attributionMaterial = null;
    this.attributionGeometry = null;
    this.attributionMesh = null;
    this.attributionPivot = null;
    this.drawAttribution = null;
    this.stickerTexture = null;
    this.stickerMesh = null;
    this.stickerBacking = null;
    this.stickerPivot = null;
    this.stickerFlatPositions = null;
    this.tapStart = null;
    this.modelLayout = null;
    this.cabinetVertices = null;
    this.channelMeshes = [];
    this.screenLayers = [];
    this.screenGlowMeshes = [];
    this.modelReady = false;
    delete this.dataset.initialized;
  }
}

if (!customElements.get('hp-story-tv')) customElements.define('hp-story-tv', HPStoryTV);
